import { useId, useState } from 'react';
import { useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { toast } from 'src/components/ui/toast';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import {
  useCreateInventoryPolicyOverride,
  useUpdateInventoryPolicyOverride,
  type EffectivePolicy,
  type InventoryPolicyOverrideRow,
} from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';
import { OverrideWindowFields } from '../components/OverrideWindowFields';
import { parseLocalDate } from '../components/overrideWindow';
import {
  useOverrideWindowState,
  type OverrideWindowState,
} from '../components/useOverrideWindowState';
import { CreatePreviewTable } from './PolicyOverridePreviews';
import {
  boolToTri,
  invalidHardStops,
  numOrUndefined,
  numText,
  overrideFieldLabel,
  triToBool,
  type PendingOverrideValues,
  type TriState,
} from './policyOverrideRows';

// Add a policy override, or edit an active one. Editing revokes the current
// row and creates its replacement, so `submit` routes to update-vs-create off
// whether the form is in edit mode. The panel owns the hook (it has to reset
// the form when the row being edited is revoked) and renders the fields.
// Every save goes through the confirm dialog, which previews the change.

interface PolicyOverrideFormTarget {
  warehouseId: string;
  /** ProductV3 key (the SKU). */
  productV3Key: string;
  productVariantId: string | null;
}

function hasOverrideValue(values: PendingOverrideValues): boolean {
  return Object.values(values).some((value) => value !== undefined);
}

function isMaxBelowMin(values: PendingOverrideValues): boolean {
  if (values.minQuantity === undefined || values.maxQuantity === undefined) {
    return false;
  }
  return values.maxQuantity < values.minQuantity;
}

function isInvalidQuantity(value: number | undefined): boolean {
  return value !== undefined && value < 0;
}

function isInvalidPackQuantity(value: number | undefined): boolean {
  return value !== undefined && !(value > 0);
}

function canSubmitOverride({
  values,
  maxBelowMin,
  safetyStockInvalid,
  minFloorInvalid,
  packQtyInvalid,
  windowComplete,
  windowOrderValid,
  hardStopsInvalid,
  pending,
}: {
  values: PendingOverrideValues;
  maxBelowMin: boolean;
  safetyStockInvalid: boolean;
  minFloorInvalid: boolean;
  packQtyInvalid: boolean;
  windowComplete: boolean;
  windowOrderValid: boolean;
  hardStopsInvalid: boolean;
  pending: boolean;
}): boolean {
  return [
    hasOverrideValue(values),
    !maxBelowMin,
    !safetyStockInvalid,
    !minFloorInvalid,
    !packQtyInvalid,
    windowComplete,
    windowOrderValid,
    !hardStopsInvalid,
    !pending,
  ].every(Boolean);
}

function overrideSuccessMessage(
  editing: boolean,
  gt: ReturnType<typeof useGT>,
): string {
  return editing
    ? gt('Override updated', { context: 'policy override toast' })
    : gt('Override created', { context: 'policy override toast' });
}

export interface PolicyOverrideFormState {
  min: string;
  setMin: (value: string) => void;
  max: string;
  setMax: (value: string) => void;
  safetyStock: string;
  setSafetyStock: (value: string) => void;
  hardMin: string;
  setHardMin: (value: string) => void;
  hardMax: string;
  setHardMax: (value: string) => void;
  hardStopsInvalid: boolean;
  minFloor: string;
  setMinFloor: (value: string) => void;
  packQty: string;
  setPackQty: (value: string) => void;
  autoTransfer: TriState;
  setAutoTransfer: (value: TriState) => void;
  autoBuy: TriState;
  setAutoBuy: (value: TriState) => void;
  /** Ends / review-by / reason, the same window the lead-time form edits. */
  window: OverrideWindowState;
  /** The active row being replaced, or null when adding a new override. */
  editing: InventoryPolicyOverrideRow | null;
  startEdit: (o: InventoryPolicyOverrideRow) => void;
  reset: () => void;
  submit: () => void;
  confirmOpen: boolean;
  setConfirmOpen: (open: boolean) => void;
  /** The fields the save would set, as the confirm preview reads them. */
  values: PendingOverrideValues;
  /** Both quantities set, but backwards: blocks the save with a message. */
  maxBelowMin: boolean;
  safetyStockInvalid: boolean;
  minFloorInvalid: boolean;
  packQtyInvalid: boolean;
  canSubmit: boolean;
  pending: boolean;
}

export function usePolicyOverrideForm(
  target: PolicyOverrideFormTarget,
): PolicyOverrideFormState {
  const gt = useGT();
  const createMutation = useCreateInventoryPolicyOverride();
  const updateMutation = useUpdateInventoryPolicyOverride();
  const windowState = useOverrideWindowState();

  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [safetyStock, setSafetyStock] = useState('');
  const [minFloor, setMinFloor] = useState('');
  const [hardMin, setHardMin] = useState('');
  const [hardMax, setHardMax] = useState('');
  const [packQty, setPackQty] = useState('');
  const [autoTransfer, setAutoTransfer] = useState<TriState>('keep');
  const [autoBuy, setAutoBuy] = useState<TriState>('keep');
  const [editing, setEditing] = useState<InventoryPolicyOverrideRow | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const values: PendingOverrideValues = {
    minQuantity: numOrUndefined(min),
    maxQuantity: numOrUndefined(max),
    safetyStock: numOrUndefined(safetyStock),
    minFloor: numOrUndefined(minFloor),
    hardMin: numOrUndefined(hardMin),
    hardMax: numOrUndefined(hardMax),
    orderMultiple: numOrUndefined(packQty),
    autoTransferEligible: triToBool(autoTransfer),
    autoBuyEligible: triToBool(autoBuy),
  };
  const maxBelowMin = isMaxBelowMin(values);
  const safetyStockInvalid = isInvalidQuantity(values.safetyStock);
  const minFloorInvalid = isInvalidQuantity(values.minFloor);
  const packQtyInvalid = isInvalidPackQuantity(values.orderMultiple);
  const pending = [createMutation.isPending, updateMutation.isPending].some(
    Boolean,
  );
  const hardStopsInvalid = invalidHardStops(values);
  const canSubmit = canSubmitOverride({
    values,
    maxBelowMin,
    safetyStockInvalid,
    minFloorInvalid,
    packQtyInvalid,
    windowComplete: windowState.isComplete,
    windowOrderValid: windowState.orderValid,
    hardStopsInvalid,
    pending,
  });

  function reset() {
    setEditing(null);
    setMin('');
    setMax('');
    setSafetyStock('');
    setMinFloor('');
    setHardMin('');
    setHardMax('');
    setPackQty('');
    setAutoTransfer('keep');
    setAutoBuy('keep');
    windowState.reset();
  }

  function startEdit(o: InventoryPolicyOverrideRow) {
    setEditing(o);
    setMin(numText(o.minQuantity));
    setMax(numText(o.maxQuantity));
    setSafetyStock(numText(o.safetyStock));
    setMinFloor(numText(o.minFloor));
    setHardMin(numText(o.hardMin));
    setHardMax(numText(o.hardMax));
    setPackQty(numText(o.orderMultiple));
    setAutoTransfer(boolToTri(o.autoTransferEligible));
    setAutoBuy(boolToTri(o.autoBuyEligible));
    windowState.adopt(o);
  }

  function submit() {
    if (!canSubmit) return;
    const payload = { ...values, ...windowState.payload };
    const handlers = {
      onSuccess: () => {
        toast.success(overrideSuccessMessage(editing !== null, gt));
        setConfirmOpen(false);
        reset();
      },
      onError: (e: Error) =>
        toast.error(
          gt('Failed to save override: {msg}', {
            context: 'policy override error toast',
            msg: e.message,
          }),
        ),
    };
    if (editing) {
      updateMutation.mutate({ overrideId: editing.id, ...payload }, handlers);
      return;
    }
    const { productVariantId: _variant, ...policyTarget } = target;
    createMutation.mutate({ ...policyTarget, ...payload }, handlers);
  }

  return {
    min,
    setMin,
    max,
    setMax,
    safetyStock,
    setSafetyStock,
    minFloor,
    setMinFloor,
    hardMin,
    setHardMin,
    hardMax,
    setHardMax,
    hardStopsInvalid,
    packQty,
    setPackQty,
    autoTransfer,
    setAutoTransfer,
    autoBuy,
    setAutoBuy,
    window: windowState,
    editing,
    startEdit,
    reset,
    submit,
    confirmOpen,
    setConfirmOpen,
    values,
    maxBelowMin,
    safetyStockInvalid,
    minFloorInvalid,
    packQtyInvalid,
    canSubmit,
    pending,
  };
}

function TriSelect({
  value,
  onChange,
}: {
  value: TriState;
  onChange: (v: TriState) => void;
}) {
  const gt = useGT();
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === 'keep' || v === 'on' || v === 'off') onChange(v);
      }}
    >
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="keep">
          {gt('No change', { context: 'policy override tri-state option' })}
        </SelectItem>
        <SelectItem value="on">
          {gt('Eligible', { context: 'policy override tri-state option' })}
        </SelectItem>
        <SelectItem value="off">
          {gt('Not eligible', { context: 'policy override tri-state option' })}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function QuantityField({
  id,
  label,
  value,
  onChange,
  disabled = false,
  invalid = false,
  invalidMessage,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Replaces the default "zero or greater" message. */
  invalidMessage?: string;
}) {
  const gt = useGT();
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-gray-500">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={invalid}
        placeholder={gt('no change', {
          context: 'policy override input placeholder',
        })}
      />
      {invalid ? (
        <p className="mt-1 text-xs text-red-600">
          {invalidMessage ??
            gt('Must be zero or greater.', {
              context: 'policy override validation error',
            })}
        </p>
      ) : null}
    </div>
  );
}

function EligibilityField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriState;
  onChange: (value: TriState) => void;
}) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <TriSelect value={value} onChange={onChange} />
    </div>
  );
}

// The window is already on screen when the dialog opens, so the message says
// what it means for the override rather than repeating the dates as fields.
function SaveConfirmDialog({
  form,
  effective,
}: {
  form: PolicyOverrideFormState;
  effective: EffectivePolicy | null;
}) {
  const gt = useGT();
  const { editing, window: overrideWindow } = form;
  return (
    <ConfirmDialog
      open={form.confirmOpen}
      onClose={() => form.setConfirmOpen(false)}
      onConfirm={form.submit}
      closeOnConfirm={false}
      loading={form.pending}
      wide
      title={
        editing
          ? gt('Replace override?', {
              context: 'update override confirm title',
            })
          : gt('Create override?', { context: 'create override confirm title' })
      }
      message={
        overrideWindow.endsAt
          ? gt('Effective until {date}. Sets the fields below.', {
              context: 'create override confirm message with end date',
              date: parseLocalDate(overrideWindow.endsAt).toLocaleDateString(),
            })
          : gt('Open-ended, to be reviewed by {date}. Sets the fields below.', {
              context: 'create override confirm message with review date',
              date: overrideWindow.reviewAt
                ? parseLocalDate(overrideWindow.reviewAt).toLocaleDateString()
                : '',
            })
      }
      confirmText={
        editing
          ? gt('Replace override', {
              context: 'update override confirm button',
            })
          : gt('Create override', {
              context: 'create override confirm button',
            })
      }
    >
      <CreatePreviewTable values={form.values} effective={effective} />
    </ConfirmDialog>
  );
}

export function PolicyOverrideForm({
  form,
  effective,
}: {
  form: PolicyOverrideFormState;
  effective: EffectivePolicy | null;
}) {
  const gt = useGT();
  const fieldId = useId();
  const { editing, window: overrideWindow } = form;
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <QuantityField
          id={`${fieldId}-min`}
          label={overrideFieldLabel('minQuantity', gt)}
          value={form.min}
          onChange={form.setMin}
        />
        <QuantityField
          id={`${fieldId}-max`}
          label={overrideFieldLabel('maxQuantity', gt)}
          value={form.max}
          onChange={form.setMax}
        />
        <QuantityField
          id={`${fieldId}-safety-stock`}
          label={overrideFieldLabel('safetyStock', gt)}
          value={form.safetyStock}
          onChange={form.setSafetyStock}
          disabled={
            effective?.baseSafetyStock == null && editing?.safetyStock == null
          }
          invalid={form.safetyStockInvalid}
        />
        <QuantityField
          id={`${fieldId}-min-floor`}
          label={overrideFieldLabel('minFloor', gt)}
          value={form.minFloor}
          onChange={form.setMinFloor}
          invalid={form.minFloorInvalid}
        />
        <QuantityField
          id={`${fieldId}-hardMin`}
          label={overrideFieldLabel('hardMin', gt)}
          value={form.hardMin}
          onChange={form.setHardMin}
          invalid={form.hardStopsInvalid}
          invalidMessage={gt(
            'Hard stops must be non-negative, with hard min at or below hard max.',
            { context: 'policy hard stop validation error' },
          )}
        />
        <QuantityField
          id={`${fieldId}-hardMax`}
          label={overrideFieldLabel('hardMax', gt)}
          value={form.hardMax}
          onChange={form.setHardMax}
          invalid={form.hardStopsInvalid}
          invalidMessage={gt(
            'Hard stops must be non-negative, with hard min at or below hard max.',
            { context: 'policy hard stop validation error' },
          )}
        />
        <QuantityField
          id={`${fieldId}-pack-qty`}
          label={overrideFieldLabel('orderMultiple', gt)}
          value={form.packQty}
          onChange={form.setPackQty}
          invalid={form.packQtyInvalid}
          invalidMessage={gt('Must be greater than zero.', {
            context: 'policy override pack qty validation error',
          })}
        />
        <p className="col-span-2 -mt-1 text-xs text-gray-500">
          {gt(
            'Pack Qty replaces the vendor master carton/MOQ for buys here — higher or lower.',
            { context: 'policy override pack qty help text' },
          )}
        </p>
        <EligibilityField
          label={overrideFieldLabel('autoTransferEligible', gt)}
          value={form.autoTransfer}
          onChange={form.setAutoTransfer}
        />
        <EligibilityField
          label={overrideFieldLabel('autoBuyEligible', gt)}
          value={form.autoBuy}
          onChange={form.setAutoBuy}
        />
        <OverrideWindowFields
          startsAt={overrideWindow.startsAt}
          onStartsAtChange={overrideWindow.setStartsAt}
          orderInvalid={!overrideWindow.orderValid}
          endsAt={overrideWindow.endsAt}
          reviewAt={overrideWindow.reviewAt}
          onEndsAtChange={overrideWindow.setEndsAt}
          onReviewAtChange={overrideWindow.setReviewAt}
          disabled={form.pending}
        />
        <div className="col-span-2">
          <Label
            htmlFor={`${fieldId}-reason`}
            className="text-xs text-gray-500"
          >
            {gt('Reason', { context: 'policy override reason field' })}
          </Label>
          <Textarea
            id={`${fieldId}-reason`}
            rows={1}
            value={overrideWindow.reason}
            onChange={(e) => overrideWindow.setReason(e.target.value)}
          />
        </div>
      </div>

      {form.maxBelowMin ? (
        <p className="mt-1 text-xs text-red-600">
          {gt('Max must be ≥ min.', {
            context: 'policy override validation error',
          })}
        </p>
      ) : null}

      {editing ? (
        <p className="mt-2 text-xs text-blue-700">
          {gt('Editing an override. Saving replaces it with a new row.', {
            context: 'policy override edit mode notice',
          })}
        </p>
      ) : null}

      <div className="mt-2 flex gap-2">
        <Button
          className="flex-1"
          size="sm"
          disabled={!form.canSubmit}
          onClick={() => form.setConfirmOpen(true)}
        >
          {editing
            ? gt('Save changes', { context: 'policy override submit button' })
            : gt('Add override', { context: 'policy override submit button' })}
        </Button>
        {editing ? (
          <Button size="sm" variant="outline" onClick={form.reset}>
            {gt('Cancel', { context: 'policy override cancel edit button' })}
          </Button>
        ) : null}
      </div>

      <SaveConfirmDialog form={form} effective={effective} />
    </>
  );
}
